"use client";

import type { PaginatedThreadsResponseDto } from "@dc/gateway-jsonapi";
import { threadsControllerFindAll } from "@dc/gateway-jsonapi";
import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";

const PAGE_SIZE = 20;

/** Prefix of every thread-list query key, so one invalidation hits them all. */
export const THREADS_INFINITE_KEY = ["threads", "infinite"] as const;

interface UseThreadsInfiniteOptions {
  teamScopeId?: string;
  search?: string;
  filterByScopeIds?: string[];
  /** Lists threads whose scope includes this file. */
  fileScopeId?: string;
  /** The fileScopeId file's project, so its threads with no file selection are listed too. */
  projectScopeId?: string;
  /**
   * Omit for every thread. Pass true or false to take only that side, which is how the
   * Threads panel keeps a pinned thread above a Recents list it would otherwise page past.
   */
  isPinned?: boolean;
  limit?: number;
  enabled?: boolean;
}

export function useThreadsInfinite({
  teamScopeId,
  search,
  filterByScopeIds,
  fileScopeId,
  projectScopeId,
  isPinned,
  limit = PAGE_SIZE,
  enabled = true,
}: UseThreadsInfiniteOptions = {}) {
  return useInfiniteQuery({
    queryKey: [
      ...THREADS_INFINITE_KEY,
      teamScopeId ?? null,
      search ?? null,
      filterByScopeIds ?? null,
      fileScopeId ?? null,
      projectScopeId ?? null,
      isPinned ?? null,
      limit,
    ],
    queryFn: ({ pageParam }): Promise<PaginatedThreadsResponseDto> =>
      threadsControllerFindAll({
        page: pageParam,
        limit,
        sort: "-updatedAt",
        ...(teamScopeId ? { teamScopeId } : {}),
        ...(search ? { search } : {}),
        ...(filterByScopeIds?.length ? { filterByScopeIds: filterByScopeIds.join(",") } : {}),
        ...(fileScopeId ? { fileScopeId } : {}),
        ...(projectScopeId ? { projectScopeId } : {}),
        ...(isPinned === undefined ? {} : { isPinned }),
      }),
    enabled,
    initialPageParam: 1,
    getNextPageParam: (lastPage) => (lastPage.meta.hasNextPage ? lastPage.meta.page + 1 : undefined),
    placeholderData: keepPreviousData,
  });
}
